import type { Chat } from '../../../components/chat/chat.types';
import type { HTMLAttributes } from 'react';

import { Heading, PrimaryButton, SubHeading, classNames } from '@movingimage-evp/mi-ui-component-library';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MessageCustomType } from '../../../components/chat/chat.types';
import { MessageForm } from '../../../components/chat/message-form';
import { NicknameForm } from '../../../components/chat/nickname-form';
import { Message } from '../../../operator/components/chat-moderation-dashboard/message';
import { removeDuplicatesByProperty } from '../../../utils/remove-duplicates-by-property';

import styles from './moderated-chat.module.css';

type Props = {
  chat: Chat;
  anonymous?: boolean;
  hasSecurityPolicy?: boolean;
  onNicknameSubmit: (nickname?: string) => void;
} & HTMLAttributes<HTMLDivElement>;

export function ModeratedChat({ chat, anonymous, hasSecurityPolicy, onNicknameSubmit, className, ...props }: Props) {
  const { t } = useTranslation();
  const messagesListRef = useRef<HTMLDivElement>(null);
  const [messageValue, setMessageValue] = useState('');

  const testId = anonymous ? 'anonymous-moderated-chat' : 'moderated-chat';
  const nickNameFormVisible = !chat.connected && !anonymous;
  const anonymousInfoVisible = !chat.connected && anonymous;
  const currentUserId = chat.user?.userId;
  const approvedMessages = chat.messages.filter((message) =>
    [MessageCustomType.FROM_MODERATOR, MessageCustomType.APPROVED_BY_MODERATOR].includes(message.customType)
  );
  const messagesByCurrentUser = chat.messages.filter((message) => message.sender.userId === currentUserId);
  const messagesToShow = removeDuplicatesByProperty([...messagesByCurrentUser, ...approvedMessages], 'messageId');

  const messagesSortedByUpdateTime = [...messagesToShow].sort((a, b) => {
    if (b.sender.userId === currentUserId || b.sender.role === 'operator') {
      return a.createdAt - b.createdAt;
    }

    return (a.updatedAt || a.createdAt) - (b.updatedAt || b.createdAt);
  });

  const handleSubmit = () => {
    sendMessage(messageValue);
  };

  const sendMessage = async (message: string) => {
    if (await chat.sendMessage(message)) setMessageValue('');
    scrollMessagesListToBottom();
  };

  const scrollMessagesListToBottom = () => {
    setTimeout(() => messagesListRef.current?.scrollTo({ top: messagesListRef.current.scrollHeight }), 0);
  };

  if (nickNameFormVisible) {
    return (
      <div data-testid={testId} className={classNames(styles.wrapper, styles.nicknameForm, className)} {...props}>
        <NicknameForm
          className={styles.nicknameForm}
          loading={chat.connecting}
          readOnly={hasSecurityPolicy}
          onSubmit={onNicknameSubmit}
        />
      </div>
    );
  }

  if (anonymousInfoVisible) {
    return (
      <div data-testid={testId} className={classNames(styles.wrapper, styles.anonymousInfo, className)} {...props}>
        <div data-testid={`${testId}-info`}>
          <Heading>{t('viewer.views.stream.chat.anonymousInfo.moderated.heading')}</Heading>
          <SubHeading>{t('viewer.views.stream.chat.anonymousInfo.moderated.subHeading')}</SubHeading>
        </div>

        <PrimaryButton data-testid={`${testId}-join-button`} onClick={() => onNicknameSubmit(undefined)}>
          {t('viewer.views.stream.chat.anonymousInfo.moderated.button')}
        </PrimaryButton>
      </div>
    );
  }

  return (
    <div data-testid={testId} className={classNames(styles.wrapper, className)} {...props}>
      <div
        ref={messagesListRef}
        data-testid="chat-messages-list"
        className={classNames(styles.messagesList, 'lspro-scrollbars')}
      >
        {messagesSortedByUpdateTime.map((message) => (
          <Message
            key={message.messageId}
            message={message}
            anonymous={anonymous}
            onVote={() => chat.voteForMessage(message.messageId)}
          />
        ))}
      </div>

      <MessageForm
        value={messageValue}
        onChange={setMessageValue}
        loading={chat.sendingMessage || chat.editingMessage}
        error={chat.userActionError}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
